import axios from 'axios';
import AxiosObservable from 'axios-observable';
import { throwError } from 'rxjs';

const TOKEN_KEY = 'admin-token';
const LOGIN_PATH = '/';
const DEFAULT_HOME = '/dossiers?paginateSize=50';
const SESSION_EXPIRED_KEY = 'session-expired';
const REDIRECT_AFTER_LOGIN_KEY = 'redirect-after-login';

function decodeTokenPayload(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(payload));
  } catch (e) {
    return null;
  }
}

function isTokenExpired(token) {
  const payload = decodeTokenPayload(token);
  if (!payload) {
    return true;
  }
  return payload.exp !== undefined && payload.exp * 1000 <= Date.now();
}

function logout() {
  localStorage.clear();
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
  window.location.replace(LOGIN_PATH);
}

function sessionExpired() {
  const { pathname, search } = window.location;
  if (pathname !== LOGIN_PATH) {
    sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, `${pathname}${search}`);
  }
  sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
  localStorage.clear();
  if (pathname !== LOGIN_PATH) {
    window.location.replace(LOGIN_PATH);
  }
}

function wasSessionExpired() {
  return sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
}

function clearSessionExpired() {
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
}

function consumeRedirectAfterLogin() {
  const target = sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
  sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
  if (target && target.startsWith('/') && !target.startsWith('//')) {
    return target;
  }
  return DEFAULT_HOME;
}

function isUnauthorized(error) {
  return Boolean(error
    && (error.status === 401 || (error.response && error.response.status === 401)));
}

function handleUnauthorized(error) {
  if (isUnauthorized(error)) {
    sessionExpired();
  }
  return throwError(() => error);
}

const onRejected = (error) => {
  if (isUnauthorized(error)) {
    sessionExpired();
  }
  return Promise.reject(error);
};

axios.interceptors.response.use((response) => response, onRejected);
// axios-observable embarque sa propre instance d'axios : l'intercepteur ci-dessus ne la couvre pas
AxiosObservable.interceptors.response.use((response) => response, onRejected);

function useAuth() {
  const token = localStorage.getItem(TOKEN_KEY);

  if (!token) {
    return { token: null, isLoggedIn: false };
  }
  if (isTokenExpired(token)) {
    sessionExpired();
    return { token: null, isLoggedIn: false };
  }
  axios.defaults.headers.common['x-access-token'] = token;
  AxiosObservable.defaults.headers.common['x-access-token'] = token;
  return { token, isLoggedIn: true };
}

export {
  useAuth,
  handleUnauthorized,
  isUnauthorized,
  isTokenExpired,
  sessionExpired,
  logout,
  wasSessionExpired,
  clearSessionExpired,
  consumeRedirectAfterLogin,
};
